import * as React from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';

import { Dialog, DialogActions, DialogContent, DialogTitle, Loading } from 'components';
import { useFetchSubscriptionPreviewForDeletingZonesMutation } from 'store/apis/billing.api';
import { useAppSelector } from 'store/store.hook';
import { selectCurrentUserInfo } from 'store/slices/auth.slice';
import { useInactivateZoneMutation } from 'store/apis';

interface ZoneDeactivateDialogProps {
  visible?: boolean;
  setVisible?: (visible: boolean) => void;
  zoneId: number;
}

export const ZoneDeactivateDialog = ({ visible = false, setVisible = () => {}, zoneId }: ZoneDeactivateDialogProps) => {
  const { orgSlug } = useAppSelector(selectCurrentUserInfo);
  const [previewSubscription, setPreviewSubscription] = React.useState<any>(null);
  const [
    fetchSubscriptionPreviewForDeletingZones,
    {
      isLoading: loading_subscriptionPreview,
      isSuccess: success_subscriptionPreview,
      isError: error_subscriptionPreview,
      data: previewSubscriptionData,
    },
  ] = useFetchSubscriptionPreviewForDeletingZonesMutation();
  const [inactivateZone, { isLoading: loading_inactivate, isSuccess: success_inactivate, isError: error_inactivate }] =
    useInactivateZoneMutation();

  React.useEffect(() => {
    fetchSubscriptionPreviewForDeletingZones({
      orgSlug,
      zoneIds: [zoneId],
    });
  }, [zoneId]);

  React.useEffect(() => {
    if (!loading_subscriptionPreview && success_subscriptionPreview && !error_subscriptionPreview) {
      setPreviewSubscription(previewSubscriptionData);
    }
  }, [loading_subscriptionPreview, success_subscriptionPreview, error_subscriptionPreview]);

  React.useEffect(() => {
    if (!loading_inactivate && success_inactivate && !error_inactivate) {
      setVisible(false);
    }
  }, [loading_inactivate, success_inactivate, error_inactivate]);

  const onClose = (event: any, reason: string) => {
    if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
      setVisible(false);
      document.body.removeAttribute('style');
    }
  };

  const onConfirm = () => {
    inactivateZone({ orgSlug, id: zoneId });
  };

  return (
    <Dialog onClose={onClose} open={visible} fullScreen={false}>
      <DialogTitle>
        <Typography variant="h6" component="span">
          Confirm Deactivate Zone
        </Typography>
      </DialogTitle>

      <DialogContent>
        {loading_subscriptionPreview ? (
          <Box
            sx={{
              width: '100%',
              height: 180,
              paddingBottom: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Box
              sx={{
                maxWidth: 300,
              }}
            >
              <Loading text="One moment, loading a preview of how this will change your subscription..." />
            </Box>
          </Box>
        ) : (
          <Box
            sx={{
              height: 105.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography variant="subtitle2" color="textSecondary">
              {previewSubscription?.message || 'Do you really want to deactivate this zone?'}
            </Typography>
          </Box>
        )}
      </DialogContent>

      {!loading_subscriptionPreview ? (
        <DialogActions>
          <Button
            onClick={() => onClose(null, 'anyway')}
            sx={{
              paddingX: 2,
              color: (theme) => theme.palette.text.secondary,
            }}
          >
            Cancel
          </Button>
          <Button
            color="primary"
            onClick={onConfirm}
            disabled={loading_inactivate}
            endIcon={loading_inactivate && <CircularProgress color="inherit" size={14} />}
            sx={{
              paddingX: 2,
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      ) : null}
    </Dialog>
  );
};
